import type { DocPageContent } from '../docs-content'

const page = {
  order: 13,
  slug: 'effects-color',
  title: '特效与调色',
  description:
    '添加并堆叠 GPU 特效、用色轮与曲线调色、读取示波器、应用 LUT，以及隔离校正。',
  category: '创意工具',
  related: ['keyframes', 'shapes-masks', 'properties'],
  sections: [
    {
      title: '添加与管理特效',
      blocks: [
        {
          kind: 'steps',
          items: [
            '选中一个片段并打开属性中的**特效**部分。',
            '使用**添加特效**，然后搜索或从分类中选择。你也可以添加内置**预设**或自己保存的预设。',
            '用**上移**和**下移**重新排序堆叠——顺序会改变特效的组合方式。',
            '可在特效面板中禁用、重置或移除某个特效；数值参数可以打关键帧。',
          ],
        },
      ],
    },
    {
      title: '特效目录',
      blocks: [
        {
          kind: 'table',
          headers: ['分类', '特效'],
          rows: [
            [
              '色彩',
              '亮度、对比度、曝光、饱和度、自然饱和度、色相偏移、色温、色阶、曲线、色轮、渐变映射、LUT',
            ],
            ['模糊', '高斯、方框、运动、径向、缩放'],
            [
              '扭曲',
              '像素化、RGB 分离、漩涡、波浪、膨胀/收缩、万花筒、镜像、凹槽玻璃、涟漪玻璃、玻璃马赛克、方块、Droste 等',
            ],
            [
              '风格化',
              '暗角、胶片颗粒、锐化、辉光、扫描线、CRT、VHS、半调、抖动、ASCII、墨迹、像素排序、方块故障、色彩故障',
            ],
            ['抠像', '色度键（绿幕与蓝幕移除）'],
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '所有特效都在 GPU（WebGPU）上渲染。近期新增的包括**渐变映射**、**VHS**、**CRT**、**Droste**、**方块故障**，以及**涟漪玻璃**与**玻璃马赛克**扭曲、**墨迹**和**像素排序**。',
        },
        {
          kind: 'note',
          tone: 'tip',
          text: '**ASCII** 可渲染其自身的字符：将**字符集**设为**自定义**，输入你的**自定义字符**（渐变从最密到最浅），并选择一种**字体**（Monospace、Courier、Consolas 或 Lucida Console）。内置集合包括 ASCII Ramp、Dense、Binary 和 Symbols。',
        },
      ],
    },
    {
      title: '颜色输入与吸管',
      blocks: [
        {
          kind: 'list',
          items: [
            '每个颜色控件都接受**十六进制**值；直接输入到字段中（输入时会实时更新预览）。',
            '在支持透明度的地方，字段接受 8 位十六进制（`#rrggbbaa`），选择器会额外提供 alpha 滑块。',
            '点击**吸管**（滴管）可从应用或预览中的任意位置取样颜色；放大镜会跟随光标，方便你精确落在某个像素上，然后点击即可应用。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '吸管是应用内置的取样器（并非浏览器的原生吸管），无需权限提示。调色工作区中的色轮使用的也是同一工具。',
        },
      ],
    },
    {
      title: '在调色工作区中调色',
      blocks: [
        {
          kind: 'list',
          items: [
            '切换到**调色**工作区（`Alt+2`）并选中一个片段；即使尚未调色，色轮与曲线也已就绪。',
            '**色轮**可调整阴影、中间调、高光和偏移，并提供白平衡选取与自动平衡。',
            '**曲线**可编辑主、红、绿、蓝通道；点击曲线添加控制点，双击控制点将其移除。',
            '用**调整前**、**调整后**和**分屏**进行对比，并用**复制调色**与**粘贴调色**在片段之间复制某种风格。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '**跳过调色**会预览未调色的画面，但永远不会影响导出。',
        },
      ],
    },
    {
      title: '读取示波器',
      blocks: [
        {
          kind: 'table',
          headers: ['示波器', '显示内容'],
          rows: [
            ['波形图', '亮度与色彩分布，自上而下'],
            ['RGB Parade', '红、绿、蓝通道并排显示'],
            ['矢量示波器', '色相与饱和度'],
            ['直方图', '色调的分布范围'],
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: '用**显示颜色示波器**切换示波器。波形图与直方图还提供 RGB、单通道以及亮度（Y）视图。',
        },
      ],
    },
    {
      title: 'LUT 与隔离校正',
      blocks: [
        {
          kind: 'list',
          items: [
            '添加**LUT（.cube）**特效，选择**导入 .cube LUT**，然后设置**强度**将其混合进去。',
            '**二级限定器**可将校正限定到一定的色相、饱和度与亮度范围。',
            '**Power Window**可将校正限制在一个椭圆或矩形遮罩内，支持羽化与反相。',
            '添加**调整图层**，以对下方轨道上的每个片段进行调色或应用特效。',
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          text: '导入 LUT 需要基于 Chromium 内核的浏览器。',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
